import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface AboutSectionProps {
  setActiveSection: (section: string) => void;
}

const AboutSection = ({ setActiveSection }: AboutSectionProps) => {
  const { ref, inView } = useInView({ 
    threshold: 0.2,
    triggerOnce: false 
  });

  useEffect(() => {
    if (inView) {
      setActiveSection('about');
    }
  }, [inView, setActiveSection]);

  const stats = [
    { number: '500+', label: 'Projects Completed' },
    { number: '98%', label: 'Client Satisfaction' },
    { number: '50+', label: 'Brand Partners' },
    { number: '24/7', label: 'Support Available' }
  ];

  return (
    <section
      id="about"
      ref={ref}
      className="relative py-16 sm:py-20 lg:py-32 px-4 sm:px-6 lg:px-12 bg-black"
    >
      <div className="max-w-[1600px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-16 items-center">
          {/* Image - Above text on mobile */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="relative order-1 lg:order-1"
          >
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] sm:aspect-square">
              <img
                src="https://public.readdy.ai/ai/img_res/17b2d93813b071e24bd614b84ee48cc1.jpg"
                alt="Black Velvet Studio"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="order-2 lg:order-2"
          >
            <p className="text-xs sm:text-sm uppercase tracking-[3px] text-brand-aqua mb-4 font-semibold">
              ABOUT US
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 sm:mb-8">
              Where AI Meets Artistry
            </h2>
            <p className="text-base sm:text-lg text-white/80 leading-relaxed mb-6 sm:mb-8">
              Black Velvet Studio combines cutting-edge artificial intelligence with human creativity to deliver exceptional visual content. Our team of AI specialists and creative professionals work together to bring your vision to life.
            </p>
            <p className="text-base sm:text-lg text-white/70 leading-relaxed mb-8 sm:mb-10">
              From concept to completion, we ensure every project reflects your brand's unique identity while leveraging the latest in AI technology.
            </p>

            {/* Stats - Stacked cards on mobile */}
            <div className="grid grid-cols-2 gap-4 sm:gap-5">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border border-white/10 text-center"
                >
                  <div className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent mb-2">
                    {stat.number}
                  </div>
                  <div className="text-xs sm:text-sm text-white/70 leading-relaxed">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
